import { format } from 'date-fns'
import { ja } from 'date-fns/locale'
import {
  AlertCircle,
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Pencil,
  RefreshCw,
  Search,
  Sparkles,
  X
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { buildCallDisplayModel, buildTranscriptSearchModel } from '../lib/callDisplay'
import { buildTodoDisplayMeta, normalizeTodos, toggleTodoAt, updateTodoAt, type EditableTodo } from '../lib/todoEditing'
import type { CallRecord, CallStatus } from '../lib/useCalls'

type Props = {
  call: CallRecord | null
  open: boolean
  onClose: () => void
  onRetry?: (call: CallRecord) => Promise<void>
  onUpdateTodos?: (callId: string, todos: EditableTodo[]) => Promise<void>
  onUpdateNextFollow?: (callId: string, nextFollowDate: string | null) => Promise<void>
}

export function CallDetailDrawer({
  call,
  open,
  onClose,
  onRetry,
  onUpdateTodos,
  onUpdateNextFollow
}: Props) {
  const [retrying, setRetrying] = useState(false)
  const [retryError, setRetryError] = useState<string | null>(null)
  const [savingTodos, setSavingTodos] = useState(false)
  const [todoError, setTodoError] = useState<string | null>(null)
  const [transcriptQuery, setTranscriptQuery] = useState('')
  const [activeMatch, setActiveMatch] = useState(0)
  const activeMatchRef = useRef<HTMLSpanElement | null>(null)
  const transcriptSearch = call?.transcript
    ? buildTranscriptSearchModel(call.transcript, transcriptQuery, activeMatch)
    : null

  useEffect(() => {
    setRetryError(null)
    setTodoError(null)
    setRetrying(false)
    setSavingTodos(false)
    setTranscriptQuery('')
    setActiveMatch(0)
  }, [call?.id])

  useEffect(() => {
    setActiveMatch(0)
  }, [transcriptQuery])

  useEffect(() => {
    activeMatchRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }, [transcriptSearch?.activeIndex, transcriptSearch?.query])

  if (!open || !call) return null

  const display = buildCallDisplayModel(call)
  const todos = normalizeTodos(call.todos)

  async function handleRetry() {
    if (!call || !onRetry) return
    setRetrying(true)
    setRetryError(null)
    try {
      await onRetry(call)
    } catch (err) {
      setRetryError(err instanceof Error ? err.message : String(err))
    } finally {
      setRetrying(false)
    }
  }

  async function saveTodos(todos: EditableTodo[]) {
    if (!call || !onUpdateTodos) return
    setSavingTodos(true)
    setTodoError(null)
    try {
      await onUpdateTodos(call.id, todos)
    } catch (err) {
      setTodoError(err instanceof Error ? err.message : String(err))
    } finally {
      setSavingTodos(false)
    }
  }

  async function saveNextFollow(value: string) {
    if (!call || !onUpdateNextFollow) return
    setTodoError(null)
    try {
      await onUpdateNextFollow(call.id, value.trim() || null)
    } catch (err) {
      setTodoError(err instanceof Error ? err.message : String(err))
    }
  }

  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        className="absolute inset-0 cursor-default bg-slate-900/20"
        aria-label="通話詳細を閉じる"
        onClick={onClose}
      />
      <aside className="absolute right-0 top-0 flex h-full w-full max-w-2xl flex-col border-l border-gray-200 bg-white shadow-2xl">
        <header className="border-b border-gray-200 px-5 py-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-xs font-medium text-gray-400">通話詳細</p>
              <h2 className="mt-1 text-lg font-semibold text-gray-900">
                {format(new Date(call.called_at), 'yyyy年M月d日 HH:mm', { locale: ja })}
              </h2>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {display.durationLabel && (
                  <span className="text-xs tabular-nums text-gray-500">{display.durationLabel}</span>
                )}
                <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${STATUS_TONE[call.status]}`}>
                  {display.statusLabel}
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-200 p-1.5 text-gray-500 hover:bg-gray-50"
              aria-label="閉じる"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </header>

        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-4">
          <section className={`rounded-lg border px-3 py-2.5 ${STATUS_PANEL_TONE[call.status]}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${STATUS_TONE[call.status]}`}>
                {display.statusLabel}
              </span>
              <p className="min-w-0 flex-1 text-[12px] leading-relaxed">{display.statusGuidance}</p>
            </div>
          </section>

          {call.status === 'failed' && (
            <section className="rounded-lg border border-red-100 bg-red-50 px-3 py-3">
              <h3 className="mb-1.5 flex items-center gap-1 text-xs font-semibold text-red-700">
                <AlertCircle className="h-3 w-3" />
                処理エラー
              </h3>
              <p className="break-words text-[12px] text-red-700">
                {call.error_message ?? '詳細なエラーメッセージはありません。'}
              </p>
              {display.retryTarget && onRetry ? (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => void handleRetry()}
                    disabled={retrying}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:cursor-wait disabled:opacity-60"
                  >
                    <RefreshCw className={`h-3 w-3 ${retrying ? 'animate-spin' : ''}`} />
                    {retrying ? '再試行中...' : '再試行'}
                  </button>
                  <span className="text-[11px] text-red-600">{display.retryHint}</span>
                </div>
              ) : (
                <p className="mt-2 text-[11px] text-red-500">
                  音声ファイルまたは文字起こしが残っていないため、Webからは再試行できません。
                </p>
              )}
              {retryError && (
                <p className="mt-2 break-words text-[11px] text-red-700">
                  再試行の開始に失敗: {retryError}
                </p>
              )}
            </section>
          )}

          {call.summary && (
            <section className="border-t border-gray-100 pt-4">
              <h3 className="mb-2 flex items-center gap-1 text-xs font-semibold text-gray-700">
                <Sparkles className="h-3 w-3 text-amber-500" />
                議事録
              </h3>
              <pre className="whitespace-pre-wrap rounded-lg border border-amber-100 bg-amber-50/40 px-3 py-2.5 font-sans text-[13px] leading-relaxed text-gray-800">
                {call.summary}
              </pre>
            </section>
          )}

          <section className="border-t border-gray-100 pt-4">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-xs font-semibold text-gray-700">TODO</h3>
              {savingTodos && <span className="text-[11px] text-gray-400">保存中...</span>}
            </div>
            {todos.length > 0 ? (
              <ul className="space-y-2 text-[13px]">
                {todos.map((todo, index) => {
                  const meta = buildTodoDisplayMeta(todo)
                  return (
                  <li key={index} className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2.5">
                    <div className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        checked={todo.completed}
                        className="mt-1"
                        onChange={() => void saveTodos(toggleTodoAt(todos, index))}
                        disabled={!onUpdateTodos || savingTodos}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-1.5 text-[10px] font-medium">
                          <span
                            className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 ${
                              todo.completed ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {todo.completed && <CheckCircle2 className="h-3 w-3" />}
                            {meta.completionLabel}
                          </span>
                          <button
                            type="button"
                            onClick={() =>
                              void saveTodos(
                                updateTodoAt(todos, index, {
                                  assignee: todo.assignee === 'manager' ? 'liver' : 'manager'
                                })
                              )
                            }
                            disabled={!onUpdateTodos || savingTodos}
                            className={`rounded px-1.5 py-0.5 text-[10px] font-medium uppercase ${
                              todo.assignee === 'manager'
                                ? 'bg-sky-100 text-sky-700'
                                : 'bg-rose-100 text-rose-700'
                            }`}
                            title="クリックで担当を切り替え"
                          >
                            担当 {meta.assigneeLabel}
                          </button>
                          <span className={`rounded px-1.5 py-0.5 ${CONFIDENCE_TONE[meta.confidenceTone]}`}>
                            {meta.confidenceLabel}
                          </span>
                          <span className="rounded bg-white px-1.5 py-0.5 text-gray-500 ring-1 ring-gray-100">
                            {meta.dueDateLabel}
                          </span>
                          <span className={`rounded px-1.5 py-0.5 ${
                            todo.evidence ? 'bg-indigo-50 text-indigo-700' : 'bg-white text-gray-400 ring-1 ring-gray-100'
                          }`}>
                            {meta.evidenceLabel}
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              const task = window.prompt('TODOを編集', todo.task)
                              if (task != null) void saveTodos(updateTodoAt(todos, index, { task }))
                            }}
                            disabled={!onUpdateTodos || savingTodos}
                            className="inline-flex items-center gap-1 rounded px-1 py-0.5 text-[11px] text-gray-500 hover:bg-white hover:text-gray-800"
                          >
                            <Pencil className="h-3 w-3" />
                            編集
                          </button>
                        </div>
                        <p className={`mt-1 break-words ${todo.completed ? 'text-gray-400 line-through' : 'text-gray-700'}`}>
                          {todo.task}
                        </p>
                        <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-gray-500">
                          <CalendarDays className="h-3 w-3" />
                          <span>期限</span>
                          <input
                            type="date"
                            value={todo.due_date ?? ''}
                            onChange={(event) =>
                              void saveTodos(updateTodoAt(todos, index, { due_date: event.target.value || null }))
                            }
                            disabled={!onUpdateTodos || savingTodos}
                            className="rounded border border-gray-200 px-1 py-0.5 text-[11px]"
                          />
                        </div>
                        {todo.evidence && (
                          <p className="mt-2 break-words border-l-2 border-indigo-100 pl-2 text-[11px] leading-relaxed text-gray-500">
                            根拠: {todo.evidence}
                          </p>
                        )}
                      </div>
                    </div>
                  </li>
                  )
                })}
              </ul>
            ) : (
              <p className="rounded-lg border border-dashed border-gray-200 px-3 py-4 text-sm text-gray-400">
                抽出済みTODOはありません。
              </p>
            )}
          </section>

          <section className="text-xs text-gray-600">
            📅 次回フォロー:{' '}
            <input
              type="date"
              value={call.next_follow_date ?? ''}
              onChange={(event) => void saveNextFollow(event.target.value)}
              disabled={!onUpdateNextFollow}
              className="ml-1 rounded border border-gray-200 px-1.5 py-0.5 text-xs"
            />
          </section>

          {todoError && (
            <p className="break-words text-[11px] text-red-600">TODO更新に失敗: {todoError}</p>
          )}

          {call.transcript && (
            <details className="border-t border-gray-100 pt-4">
              <summary className="cursor-pointer text-xs font-semibold text-gray-600 hover:text-gray-800">
                transcript全文を展開
              </summary>
              <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
                <label className="relative flex-1">
                  <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                  <input
                    type="search"
                    value={transcriptQuery}
                    onChange={(event) => setTranscriptQuery(event.target.value)}
                    placeholder="transcriptを検索"
                    className="w-full rounded-lg border border-gray-200 py-1.5 pl-7 pr-2 text-[12px] outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
                  />
                </label>
                <div className="flex items-center gap-2 text-[11px] text-gray-500">
                  <span className="min-w-[4.5rem] tabular-nums">
                    {transcriptSearch?.totalMatches
                      ? `${transcriptSearch.activeIndex + 1}/${transcriptSearch.totalMatches} 件`
                      : transcriptQuery.trim()
                        ? '0 件'
                        : '検索なし'}
                  </span>
                  <button
                    type="button"
                    onClick={() => setActiveMatch((current) => current - 1)}
                    disabled={!transcriptSearch?.totalMatches}
                    className="rounded border border-gray-200 p-1 text-gray-500 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label="前の検索結果"
                  >
                    <ChevronLeft className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveMatch((current) => current + 1)}
                    disabled={!transcriptSearch?.totalMatches}
                    className="rounded border border-gray-200 p-1 text-gray-500 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label="次の検索結果"
                  >
                    <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              <pre className="mt-2 max-h-96 overflow-y-auto rounded border border-gray-100 bg-gray-50 p-3 font-mono text-[12px] whitespace-pre-wrap text-gray-700">
                {(transcriptSearch?.segments ?? []).map((segment, index) =>
                  segment.matchIndex == null ? (
                    <span key={index}>{segment.text}</span>
                  ) : (
                    <mark
                      key={index}
                      ref={segment.active ? activeMatchRef : undefined}
                      className={segment.active ? 'bg-amber-300 text-gray-950' : 'bg-amber-100 text-gray-900'}
                    >
                      {segment.text}
                    </mark>
                  )
                )}
              </pre>
            </details>
          )}
        </div>
      </aside>
    </div>
  )
}

const STATUS_TONE: Record<CallStatus, string> = {
  pending: 'text-gray-500 bg-gray-100',
  recording: 'text-rose-700 bg-rose-50',
  transcribing: 'text-sky-700 bg-sky-50',
  awaiting_summary: 'text-amber-700 bg-amber-50',
  summarizing: 'text-violet-700 bg-violet-50',
  complete: 'text-emerald-700 bg-emerald-50',
  failed: 'text-red-700 bg-red-50'
}

const STATUS_PANEL_TONE: Record<CallStatus, string> = {
  pending: 'border-gray-200 bg-gray-50 text-gray-600',
  recording: 'border-rose-100 bg-rose-50 text-rose-700',
  transcribing: 'border-sky-100 bg-sky-50 text-sky-700',
  awaiting_summary: 'border-amber-100 bg-amber-50 text-amber-700',
  summarizing: 'border-violet-100 bg-violet-50 text-violet-700',
  complete: 'border-emerald-100 bg-emerald-50 text-emerald-700',
  failed: 'border-red-100 bg-red-50 text-red-700'
}

const CONFIDENCE_TONE: Record<NonNullable<ReturnType<typeof buildTodoDisplayMeta>['confidenceTone']>, string> = {
  high: 'bg-emerald-50 text-emerald-700',
  medium: 'bg-amber-50 text-amber-700',
  low: 'bg-red-50 text-red-700',
  unknown: 'bg-white text-gray-400 ring-1 ring-gray-100'
}
